import { FRAMED_SERVICE_SECTION_OPTIONS } from '../../capabilities/service/ServiceBlock';
import type { SkuResourceModulesState } from '../../platform/scene-layout/types';
import { buildDefaultDirectResourceModules } from '../../platform/scene-layout/utils/resolveModuleDefaults';
import type { DeploySceneConfig } from '../../platform/scenes/types';
import { EBM_MODULES } from './modules';

export const EBM_ALL_MODULES = EBM_MODULES.map((m) => m.key);

/** SKU 弹窗能力模块顺序：资源选择在服务配置之前 */
export const EBM_SKU_MODULES = ['systemSetting', 'resource', 'service', 'dns'] as const;

export type EbmSkuModuleKey = (typeof EBM_SKU_MODULES)[number];

export const EBM_SKU_MODULE_LIST: string[] = [...EBM_SKU_MODULES];

export function isEbmSkuModuleKey(moduleKey: string): moduleKey is EbmSkuModuleKey {
  return (EBM_SKU_MODULES as readonly string[]).includes(moduleKey);
}

export interface EbmDirectCreateStep {
  key: string;
  title: string;
  modules: string[];
}

/** 弹性裸金属直建合并「资源配置」步可见模块（资源选择在服务配置之前） */
export const EBM_DIRECT_MERGED_MODULES = ['baseInfo', 'network', 'systemSetting', 'resource', 'service', 'dns'] as const;

export type EbmDirectMergedModuleKey = (typeof EBM_DIRECT_MERGED_MODULES)[number];

export function filterEbmDirectMergedModules<T extends { key: string }>(modules: readonly T[]): T[] {
  const set = new Set<string>(EBM_DIRECT_MERGED_MODULES);
  return modules.filter((mod) => set.has(mod.key));
}

/** 下单页模块：直建合并去掉 baseInfo */
export const EBM_ORDER_MODULES = EBM_DIRECT_MERGED_MODULES.filter(
  (key): key is Exclude<EbmDirectMergedModuleKey, 'baseInfo'> => key !== 'baseInfo',
);

export const EBM_SCENES: Record<'direct' | 'sku' | 'order', DeploySceneConfig> = {
  direct: {
    modules: [...EBM_ALL_MODULES],
    capabilityOptions: {
      service: FRAMED_SERVICE_SECTION_OPTIONS,
    },
    linkage: true,
  },
  sku: {
    modules: [...EBM_SKU_MODULES],
    capabilityOptions: {
      service: FRAMED_SERVICE_SECTION_OPTIONS,
    },
    linkage: true,
  },
  order: {
    modules: [...EBM_ORDER_MODULES],
    capabilityOptions: {},
    linkage: true,
  },
};

export function resolveEbmSceneConfig(scene?: 'direct' | 'sku' | 'order'): DeploySceneConfig {
  return EBM_SCENES[scene ?? 'direct'];
}

/** 弹性裸金属直建：单步「资源配置」 */
export const EBM_DIRECT_CREATE_STEPS: EbmDirectCreateStep[] = [
  { key: 'resourceConfig', title: '资源配置', modules: [...EBM_DIRECT_MERGED_MODULES] },
];

function hasDnsConfig(dnsConfig: unknown): boolean {
  if (!dnsConfig || typeof dnsConfig !== 'object') return false;
  const record = dnsConfig as Record<string, unknown>;
  const listKeys = ['nameservers', 'searches', 'options'] as const;
  return listKeys.some((key) => {
    const list = record[key];
    return Array.isArray(list) && list.length > 0;
  });
}

/** 编辑回显：按已有表单数据推断直建模块勾选状态 */
export function buildEbmDirectResourceModulesFromForm(values: Record<string, unknown>): SkuResourceModulesState {
  const defaults = buildDefaultDirectResourceModules('ElasticBareMetal');
  const network = (values.resource as Record<string, unknown> | undefined)?.network;
  const networkRecord = network && typeof network === 'object' ? (network as Record<string, unknown>) : undefined;
  const hasDns = hasDnsConfig(networkRecord?.dnsConfig);
  /** 已有服务端口时回填勾选服务配置模块 */
  const hasServices = Array.isArray(networkRecord?.service) && (networkRecord.service as unknown[]).length > 0;

  return {
    ...defaults,
    ...(hasDns ? { dns: true } : {}),
    ...(hasServices ? { service: true } : {}),
  };
}

export function filterEbmSkuModules<T extends { key: string }>(modules: readonly T[]): T[] {
  const set = new Set<string>(EBM_SKU_MODULES);
  return modules.filter((mod) => set.has(mod.key));
}
